import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import FormatColorResetIcon from '@mui/icons-material/FormatColorReset'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import WaterDamageIcon from '@mui/icons-material/WaterDamage'
import WaterDropIcon from '@mui/icons-material/WaterDrop'
import { Box, Grid, type SvgIconTypeMap, Typography } from '@mui/material'
import { type OverridableComponent } from '@mui/material/OverridableComponent'
import { CONTACT_US_LINK, ROUTES_PATH, ServicesFooterButtonData } from 'config'
import { color } from 'config/color'
import { useAuth } from 'context'
import type React from 'react'
import { useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useMobileBreakpoints } from 'utils/hooks/useMobileBreakpoints'
import { ServiceFooterButton } from './component/ServiceFooterButton'
import ServiceForm, { FormType } from './component/ServiceForm'

interface ShowFormType {
  start: boolean
  stop: boolean
  transfer: boolean
  newTap: boolean
}

export const Services: React.FC = () => {
  const isMobile = useMobileBreakpoints()
  const { account } = useAuth()
  const [showForm, setShowForm] = useState({
    start: false,
    stop: false,
    transfer: false,
    newTap: false
  })
  const [showServiceButton, setShowServiceButton] = useState(true)
  const hasShowForm = useMemo(() => {
    return Object.values(showForm).some((value) => value)
  }, [showForm])
  const location = useLocation()

  const handleClick = (formKey: keyof ShowFormType): void => {
    setShowForm((prevShowForm) => ({
      ...prevShowForm,
      [formKey]: !prevShowForm[formKey]
    }))
    setShowServiceButton(!showServiceButton)
  }

  return (
    <Box mt={3} mb={!hasShowForm ? 25 : 10} bgcolor={color.white} width="100%">
      {showServiceButton && (
        <Box p={isMobile ? 3 : 5}>
          <Typography
            fontSize={isMobile ? 18 : 20}
            fontWeight={600}
            color="#010B0E"
            variant="h2"
          >
            What would you like to do?
          </Typography>
          <Typography fontSize={14} color="#5D6365">
            Manage your services with the following options:
          </Typography>
          <Box
            display="flex"
            flexDirection={isMobile ? 'column' : 'row'}
            gap={2}
            my={2}
          >
            <ServiceButton
              title="Start service"
              icon={WaterDropIcon}
              onClick={() => {
                handleClick('start')
              }}
            />
            <ServiceButton
              title="Stop service"
              icon={FormatColorResetIcon}
              onClick={() => {
                handleClick('stop')
              }}
            />
            <ServiceButton
              title="Transfer service"
              icon={ArrowForwardIcon}
              onClick={() => {
                handleClick('transfer')
              }}
            />
            <ServiceButton
              title="Request new tap"
              icon={WaterDamageIcon}
              onClick={() => {
                handleClick('newTap')
              }}
            />
          </Box>
        </Box>
      )}
      {showForm.start && (
        <ServiceForm
          isAuthenticated={location.pathname === ROUTES_PATH.AUTHORIZED_SERVICES}
          title="Start service"
          desc="Fill out the form to request water service at your new address. Once submitted, our team will process your request and get your service started."
          formType={FormType.Start}
          handleClick={() => {
            handleClick('start')
          }}
        />
      )}
      {showForm.stop && (
        <ServiceForm
          isAuthenticated={location.pathname === ROUTES_PATH.AUTHORIZED_SERVICES}
          title="Stop service"
          desc="Submit a request to discontinue water service at your current address."
          formType={FormType.Stop}
          handleClick={() => {
            handleClick('stop')
          }}
        />
      )}
      {showForm.transfer && (
        <ServiceForm
          isAuthenticated={location.pathname === ROUTES_PATH.AUTHORIZED_SERVICES}
          title="Transfer service"
          desc="Request to transfer your water service from your current address to a new location."
          formType={FormType.Transfer}
          handleClick={() => {
            handleClick('transfer')
          }}
        />
      )}
      {showForm.newTap && (
        <ServiceForm
          isAuthenticated={location.pathname === ROUTES_PATH.AUTHORIZED_SERVICES}
          title="Request new tap"
          desc="Apply for the installation of a new water tap at your property."
          formType={FormType.NewTap}
          handleClick={() => {
            handleClick('newTap')
          }}
        />
      )}

      {!hasShowForm && (
        <Box p={isMobile ? 3 : 5}>
          <Typography
            fontSize={18}
            fontWeight={600}
            color={color.grey[900]}
            mb={2}
            variant="h2"
          >
            Didn&apos;t find what you were looking for? Visit our FAQ page or
            contact us directly.
          </Typography>
          <Box
            display="flex"
            gap={2}
            flexDirection={isMobile ? 'column' : 'row'}
          >
            {ServicesFooterButtonData.map((item, index) => (
              <ServiceFooterButton
                key={index}
                title={item.title}
                desc={item.desc}
                link={account != null && index === 0 ? ROUTES_PATH.AUTHORIZEDFAQS : account != null && index === 1 ? CONTACT_US_LINK : item.link}
                linkTitle={item.linkTitle}
                icon={item.icon}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  )
}

interface ServiceButtonProps {
  icon: OverridableComponent<SvgIconTypeMap<unknown, 'svg'>> | null
  desc?: string
  title: string
  onClick: () => void
}

export const ServiceButton: React.FC<ServiceButtonProps> = (
  props: ServiceButtonProps
) => {
  const isMobile = useMobileBreakpoints()
  return (
    <Grid
      flex={1}
      item
      xs={isMobile ? 12 : 4}
      sx={{ textDecoration: 'none', cursor: 'pointer' }}
      onClick={() => {
        props.onClick()
      }}
      role="button"
      tabIndex={0}
      aria-label={props.title}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          props.onClick()
        }
      }}
    >
      <Box
        p={2}
        border={`1px solid ${color.background[300]}`}
        borderRadius={1}
        display="flex"
        flexDirection="column"
        height="80%"
      >
        <Box display="flex" alignItems="center" justifyContent="flex-end">
          <OpenInNewIcon
            sx={{ color: color.grey[400], width: 24, height: 24 }}
            aria-hidden="true"
          />
        </Box>
        <Box mt={2}>
          {props.icon !== null && (
            <props.icon
              sx={{ width: 40, height: 40, color: color.primary[300] }}
              aria-hidden="true"
            />
          )}
          <Typography fontSize={18} color={color.grey[700]}>
            {props.title}
          </Typography>
        </Box>
      </Box>
    </Grid>
  )
}
